/**
 * Field Context Utilities
 * 
 * This file provides helper functions for working with field contexts
 * to ensure consistency between frontend and backend.
 */

/**
 * Field Context Constants
 * These match the backend FieldContext enum values
 */
export const FIELD_CONTEXT = {
  IDS_CONTEXT: "IDS_CONTEXT",      // Individual Data System (1)
  PDS_CONTEXT: "PDS_CONTEXT",      // Pedigree Data System (2)
  INVITES_CONTEXT: "INVITES_CONTEXT" // Invites Context (5)
} as const;

export type FieldContextType = typeof FIELD_CONTEXT[keyof typeof FIELD_CONTEXT];

/**
 * Field Context Mapping
 * Maps numeric context values to string constants
 */
export const FIELD_CONTEXT_MAP = {
  1: FIELD_CONTEXT.IDS_CONTEXT,
  2: FIELD_CONTEXT.PDS_CONTEXT,
  5: FIELD_CONTEXT.INVITES_CONTEXT
} as const;

/**
 * Determines the appropriate field context based on various factors
 * @param isPss - Whether this is a pedigree spreadsheet
 * @param fieldType - The type of field
 * @param systemField - Whether this is a system field
 * @returns The appropriate field context string
 */
export function determineFieldContext(
  isPss: boolean = false,
  fieldType?: number,
  systemField: boolean = false
): FieldContextType {
  // For pedigree spreadsheets, use PDS context
  if (isPss) {
    return FIELD_CONTEXT.PDS_CONTEXT;
  }
  
  // For system fields, default to IDS context
  if (systemField) {
    return FIELD_CONTEXT.IDS_CONTEXT;
  }
  
  // Default to IDS context for individual data
  return FIELD_CONTEXT.IDS_CONTEXT;
}

/**
 * Ensures a field object has the required fieldcontext field
 * @param field - The field object to validate
 * @param defaultContext - Default context to use if missing
 * @returns The field object with fieldcontext populated
 */
export function ensureFieldContext<T extends { fieldcontext?: string }>(
  field: T,
  defaultContext: FieldContextType = FIELD_CONTEXT.IDS_CONTEXT
): T & { fieldcontext: string } {
  if (!field.fieldcontext) {
    return {
      ...field,
      fieldcontext: defaultContext
    };
  }
  return field as T & { fieldcontext: string };
}

/**
 * Validates that a field context string is valid
 * @param context - The context string to validate
 * @returns True if valid, false otherwise
 */
export function isValidFieldContext(context: string): context is FieldContextType {
  return Object.values(FIELD_CONTEXT).includes(context as FieldContextType);
}

/**
 * Gets the numeric value for a field context string
 * @param context - The context string
 * @returns The numeric value or null if invalid
 */
export function getFieldContextNumericValue(context: string): number | null {
  const contextMap = {
    [FIELD_CONTEXT.IDS_CONTEXT]: 1,
    [FIELD_CONTEXT.PDS_CONTEXT]: 2,
    [FIELD_CONTEXT.INVITES_CONTEXT]: 5
  };
  return contextMap[context as FieldContextType] || null;
}
